import { Suspense, use } from "react";
import type { HydrationState } from "react-router";
import { escapeHydrationJson } from "./index";

const MARKER = "__hallyulyticsDeferred";
const STORE = "__HALLYULYTICS_DEFERRED__";

type Settlement = { readonly value?: unknown } | { readonly error: true };
type Waiter = { readonly settle: (settlement: Settlement) => void };

declare global {
  interface Window {
    __staticRouterHydrationData?: HydrationState;
    [STORE]?: Record<string, Settlement | Waiter>;
  }
}

export interface DeferredValue {
  readonly id: string;
  readonly settlement: Promise<Settlement>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

function mapValues(
  record: Record<string, unknown>,
  map: (value: unknown, key: string) => unknown,
): Record<string, unknown> {
  return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, map(value, key)]));
}

/**
 * React Router's data-mode hydration JSON-serializes loader data, which turns top-level
 * loader promises into `{}`. Replace them with markers and stream their settlements instead.
 */
export function dehydrateDeferred(loaderData: Record<string, unknown>): {
  loaderData: Record<string, unknown>;
  deferred: DeferredValue[];
} {
  const deferred: DeferredValue[] = [];
  const serializable = mapValues(loaderData, (data, routeId) =>
    isPlainObject(data)
      ? mapValues(data, (value, key) => {
          if (!(value instanceof Promise)) return value;
          const id = `${routeId}:${key}`;
          deferred.push({
            id,
            settlement: value.then(
              (resolved: unknown) => ({ value: resolved }),
              () => ({ error: true }) as const,
            ),
          });
          return { [MARKER]: id };
        })
      : data,
  );
  return { loaderData: serializable, deferred };
}

function DeferredScript({ value, nonce }: { value: DeferredValue; nonce: string }) {
  const settlement = use(value.settlement);
  const script = `(function(s,i,r){var w=s[i];s[i]=r;if(w&&w.settle)w.settle(r)})(self.${STORE}=self.${STORE}||{},${escapeHydrationJson(value.id)},${escapeHydrationJson(settlement)})`;
  return <script nonce={nonce} dangerouslySetInnerHTML={{ __html: script }} />;
}

export function DeferredScripts({
  deferred,
  nonce,
}: {
  deferred: readonly DeferredValue[];
  nonce: string;
}) {
  return deferred.map((value) => (
    <Suspense key={value.id} fallback={null}>
      <DeferredScript value={value} nonce={nonce} />
    </Suspense>
  ));
}

function deferredValue(id: string): Promise<unknown> {
  const store = (window[STORE] ??= {});
  return new Promise((resolve, reject) => {
    const settle = (settlement: Settlement) =>
      "error" in settlement
        ? reject(new Error("Deferred loader value failed"))
        : resolve(settlement.value);
    const current = store[id];
    if (current && !("settle" in current)) settle(current);
    else store[id] = { settle };
  });
}

function isMarker(value: unknown): value is { [MARKER]: string } {
  return isPlainObject(value) && typeof value[MARKER] === "string";
}

/** Restores streamed loader promises in `window.__staticRouterHydrationData` before router creation. */
export function rehydrateDeferred(): void {
  const state = window.__staticRouterHydrationData;
  if (!state?.loaderData) return;
  window.__staticRouterHydrationData = {
    ...state,
    loaderData: mapValues(state.loaderData, (data) =>
      isPlainObject(data)
        ? mapValues(data, (value) => (isMarker(value) ? deferredValue(value[MARKER]) : value))
        : data,
    ),
  };
}
